/**
 * GrantRoute — the grant page, mounted.
 *
 * One read, one write. Everything on screen comes from `/authorize/preview`;
 * the decision goes back through `/authorize/approve`. The page holds no
 * opinion about which of the 22 edges it is showing — `edgeFor` reads that off
 * the server's counterparty kind, `grantOrder` decides which components appear,
 * and `useGrant` derives the rest.
 *
 * Mounted at /grant, alongside the existing /authorize rather than replacing
 * it: every edge has to move before the old page can be retired, and a
 * half-migrated consent screen is the one thing worse than an old one.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import api from '../../services/api';
import { accessBlobToYaml } from '../../utils/formAConverters';
import { edgeFor, sequenceFor } from '../authorization/grantOrder';
import { useAuth } from '../../contexts/AuthContext';
import { wrapPrivateKeyForApp } from '../../utils/fieldEncryption';
import { handBackTarget } from '../../components/grant/returnTarget';
import { useGrant } from '../../components/grant/useGrant';
import ActingChoice from '../../components/grant/ActingChoice';
import { actorsFor, hasChoice } from '../../components/grant/actors';
import { submitPlan, expiresAtFrom, EXPIRY, canKeep, grantsFromRealms,
         expiryOptionsFor, UNDATED_KINDS,
         nodeShareAccess } from '../../components/grant/consent';
import { buildAppCallbackUrl } from '../../components/grant/appCallback';
import { isFresh, clearFresh } from '../../utils/joinFlow';
import { loginUrlFor } from '../../utils/loginRedirect';
import GrantPage, { GrantFrame } from '../../components/grant/GrantPage';
import { AppMark, hasAppFigure, ELEMENTS } from '@ofself/shell';
import FaceChoice from '../../components/grant/FaceChoice';
import { categorizeDlr } from '../../utils/humanizeDlr';
import Ask, { AskProse } from '../../components/grant/Ask';
import { readNotes } from '../../nucleus/filters';
import {
  PriceLine, Explanation, Why, ActingAs, Adoption, RealmChooser, RealmLocked, Item, Verdict, Shortfall,
  Placement, SharedRealmOffer,
  Boundary, Unlock, Risk, Decide, Section, Receipt, Bond, GroupStanding,
} from '../../components/grant/parts';
import { realmLinesForYou } from '../nucleus/groupRealms';
import './GrantRoute.css';
import { LayoutGrid, User, Users } from 'lucide-react';
import { dateOf, fmt } from '../../nucleus/format';
import { ConfirmProvider } from '../../components/nucleus/actions';
import { RequestView } from '../plugins/RequestSheet';
import { requestsApi, pluginsApi } from '../plugins/data/api';
import { requestItem } from '../plugins/data/items';


// who is asking, as a glyph of what kind of thing it is — never its letters (DESIGN.md, "Faces")
const whoGlyph = (who) => { const Icon = who?.kind === 'group' ? Users : who?.kind === 'app' ? LayoutGrid : User; return <Icon size={18} strokeWidth={1.6} aria-hidden="true" />; };

/** The headcount, in each edge's own noun.
 *  "12 members" for a group you are joining; "480 people have this connected"
 *  for an app. The server sends the noun so the page never has to infer it. */
function peopleLine(who) {
  const n = fmt(who.user_count);
  const one = who.user_count === 1;
  if (who.user_noun === 'member') return `${n} ${one ? 'member' : 'members'}`;
  return `${n} ${one ? 'person has' : 'people have'} this connected`;
}

/** The register entry for a requester, when the ecosystem knows it: matched by
 *  name (ignoring a trailing "(local)" or similar) or by site. Only used to draw
 *  the app's OWN mark; an app the register does not hold gets none. */
function registerEntry(who) {
  if (!who) return null;
  const name = String(who.kind === 'sub_entity' ? who.parent_app_name : who.name || '')
    .replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();
  const host = (u) => { try { return new URL(u).hostname; } catch { return null; } };
  return ELEMENTS.find((e) => e.name.toLowerCase() === name
    || (who.website_url && host(e.url) && host(e.url) === host(who.website_url))) || null;
}

/* ONE PARKED RUN OF A PLUGIN (`/grant?request_id=`, no client_id) is not an
 * app asking for part of your identity: it is one run waiting for a yes. It is
 * drawn as that — who asked, the tool, why, its inputs, the realm, what it
 * would touch — with Allow and Decline (a decline is recorded). A link the
 * request list does not know (a JIT batch's token, an older server) is the
 * app-authorisation page below, unchanged. */
export default function GrantRoute() {
  const [params] = useSearchParams();
  const requestId = params.get('request_id');
  if (requestId && !params.get('client_id')) return <PluginRunRoute requestId={requestId} />;
  return <AppGrantRoute />;
}

function PluginRunRoute({ requestId }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ raw: null, error: null, fallback: false });
  const [fields, setFields] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    setState((s) => ({ ...s, error: null }));
    requestsApi.get(null, requestId)
      .then((r) => { if (alive) setState({ raw: r?.request || r, error: null, fallback: false }); })
      .catch((e) => {
        if (!alive) return;
        // not one the request list knows: the page it always was
        if (e?.status === 404 || e?.status === 405 || e?.status === 501) setState({ raw: null, error: null, fallback: true });
        else setState({ raw: null, error: e, fallback: false });
      });
    return () => { alive = false; };
  }, [requestId, user?.id, tick]);
  const raw = state.raw;
  useEffect(() => {
    if (!raw?.plugin?.id) return undefined;
    let alive = true;
    pluginsApi.setupConfig(raw.plugin.id, raw.group_id || null, raw.tool?.id || null)
      .then((c) => { if (alive) setFields((c?.fields || []).filter((f) => !f.secret)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [raw?.plugin?.id, raw?.group_id, raw?.tool?.id]);

  if (!authLoading && !user) return <Navigate to={loginUrlFor(location)} replace />;
  if (state.fallback) return <AppGrantRoute />;
  if (state.error) {
    return (
      <GrantFrame className="gp-route">
        <div className="gp-route--error" role="alert">
          {state.error.message || 'Could not load this request.'}{' '}
          <button type="button" className="nx-link" onClick={() => setTick((t) => t + 1)}>Try again</button>
        </div>
      </GrantFrame>
    );
  }
  if (!raw) return <GrantFrame className="gp-route" data-testid="loading" aria-busy="true" />;
  const request = requestItem(raw);
  const space = raw.group_id ? `/group/${encodeURIComponent(raw.group_id)}` : '';
  return (
    <ConfirmProvider>
      <GrantFrame className="gp-route gp-run" title={raw.status === 'pending' ? 'Waiting for you' : 'Asked to run'}>
        <div className="gp-run__body">
          <RequestView
            request={request} groupId={raw.group_id || null} fields={fields}
            onOpenNode={(id) => navigate(`${space}/graph?open=${encodeURIComponent(id)}`)}
            onOpenRun={(id) => navigate(`${space}/plugins/runs?open=${encodeURIComponent(id)}`)}
          />
        </div>
      </GrantFrame>
    </ConfirmProvider>
  );
}

function AppGrantRoute() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  /* WHOSE GRANT THIS WOULD BE — WRITTEN TO THE URL, not held beside it.
   *
   * It began as component state, which put the owner in two places and made the
   * URL the stale one. Three things read the address rather than the state: a
   * reload, the back button, and `cancel_url` on the Stripe hand-off — so a
   * buyer who picked a group, went to checkout and cancelled came back to the
   * personal page and would have minted a personal grant for a group purchase.
   *
   * `replace`, not push: choosing an owner is not a navigation, and pushing
   * would make Back walk backwards through every press. */
  const setActor = useCallback((groupId) => {
    const next = new URLSearchParams(params);
    if (groupId) next.set('group_id', groupId);
    else next.delete('group_id');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const query = useMemo(() => ({
    clientId: params.get('client_id') || undefined,
    userId: params.get('user_id') || undefined,
    toGroupId: params.get('to_group_id') || undefined,
    isPublic: params.get('public') === 'true',
    groupId: params.get('group_id') || undefined,
    subEntityKey: params.get('sub_entity_key') || undefined,
    // A follow request and a plugin run are counterparties in their own right;
    // node and edit narrow one. All four were read here and then dropped before
    // the call, so those links named no counterparty and 400'd.
    followId: params.get('follow_id') || undefined,
    requestId: params.get('request_id') || undefined,
    nodeId: params.get('node_id') || undefined,
    editAuthId: (params.get('edit_auth_id') || params.get('edit_ep_id')) || undefined,
    shareToRealm: params.get('share_to_realm') === 'true' || undefined,
    realmHint: params.get('realm_hint') || undefined,
    // `group_hint` is the group the note goes TO. Not `group_id`, which on this
    // page means "I am acting for this group" — a different question with the
    // same noun, and reading one as the other would share a personal note out of
    // a group's graph.
    shareToGroup: params.get('share_to_group') === 'true' || undefined,
    groupHint: params.get('group_hint') || undefined,
    invitationId: params.get('invitation_id') || undefined,
    // Sent to be vetted, and read back as `return_to`. `return_url` is ink's spelling.
    redirectUri: params.get('redirect_uri') || params.get('return_url') || undefined,
  }), [params]);
  const nodeId = params.get('node_id') || null;
  // `return_url` is ink's spelling of the same thing. Accepted here rather than
  // normalised by a redirect, so /authorize can render this page in place.
  // What the LINK asked for — never navigated to as it stands. `redirectUri`,
  // below, is the preview's vetted answer (components/grant/returnTarget.js).
  const askedReturn = params.get('redirect_uri') || params.get('return_url') || null;

  const { unlockEncKeys, encKeys, user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [preview, setPreview] = useState(null);
  // Where the page may send the person afterwards: the address the server vetted,
  // or nothing. Every hand-back, approve and decline below reads this one.
  const redirectUri = useMemo(() => handBackTarget(askedReturn, preview), [askedReturn, preview]);
  // The backend hands errors to this page rather than rendering its own, so an
  // `error` in the query is a real message for the person, not a stray param.
  const [loadError, setLoadError] = useState(
    params.get('error_description') || params.get('error') || null,
  );
  const [submitting, setSubmitting] = useState(false);

 const [submitError, setSubmitError] = useState(null);
  const [interval, setInterval] = useState(undefined);
  /** The itemised half of a rejection — which type and verb fell outside the realms.
   *  Held apart from the message because they answer different questions: the message
   *  says the save was refused, these say what to change. */
  const [submitViolations, setSubmitViolations] = useState(null);
  const [needsKey, setNeedsKey] = useState(false);
  /* Prose | Table: two readings of one ask. Not remembered — every visit opens
     on the prose, the part written for people. */
  const [view, setView] = useState('prose');
  /* The realm being considered (hovered or focused), previewed on the ask. */
  const [previewRealm, setPreviewRealm] = useState(null);
  const [keyError, setKeyError] = useState(null);

  /* THE OWNER CHANGED, SO NOTHING ON SCREEN DESCRIBES THIS GRANT ANY MORE.
   *
   * Until the actor could be chosen, this page fetched once per address and
   * neither of these could go stale. Now a press refetches, and both become
   * wrong in a way that matters:
   *
   *  - the PREVIEW. Left standing, C2 names the new group over the old owner's
   *    ask, realms and receipt, and Approve stays live the whole time — the
   *    page states one owner and would submit the other's plan. That is exactly
   *    the lie C2 exists to prevent, so the page drops to its loading frame and
   *    says nothing until it can say the truth.
   *  - the ERROR. `loadError` was set once and never cleared because nothing
   *    could retry. A 403 from a group whose admin rights lapsed since the
   *    groups call would replace a perfectly good consent screen with a bare
   *    error and no way back but a manual reload.
   *
   * Keyed on the OWNER, not on `query`: an unrelated param edit must not blank
   * a page the person is reading. */
  const owner = query.groupId || 'me';
  const lastOwner = useRef(owner);
  /** The last owner that previewed successfully — what a failed switch returns to. */
  const goodOwner = useRef(null);
  /** A switch that failed. NOT `loadError`: see below. */
  const [switchError, setSwitchError] = useState(null);

  useEffect(() => {
    if (lastOwner.current === owner) return;
    lastOwner.current = owner;
    setPreview(null);
    setLoadError(null);
    // NOT `setSwitchError(null)` here. A failed switch bounces the owner back,
    // and that bounce is itself an owner change — so clearing here wiped the
    // message in the very next commit after it was set. It is cleared by the
    // person's next press instead (`pickActor`), which is the thing it answers.
  }, [owner]);

  /** What the C2 link calls: a press clears the last press's failure. The
   *  bounce-back calls `setActor` directly, so it never clears its own message. */
  const pickActor = useCallback((groupId) => {
    setSwitchError(null);
    setActor(groupId);
  }, [setActor]);

  /* BACK FROM MAKING A REALM. When none of your realms fit, the page sends you to
   * make one in another tab; coming back to this one reads your realms again, so
   * the new realm is there to choose without a reload. */
  const [seen, setSeen] = useState(0);
  const [madeOne, setMadeOne] = useState(false);
  useEffect(() => {
    if (!madeOne) return undefined;
    const back = () => { if (document.visibilityState === 'visible') setSeen((n) => n + 1); };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, [madeOne]);

  useEffect(() => {
    if (params.get('error') || params.get('error_description')) return undefined;
    // Nobody signed in: there is nothing to preview, and asking would only 401
    // into a full-page reload to /login (see the signed-out return below).
    if (!user?.id) return undefined;
    let alive = true;
    api.getGrantPreview(query)
      .then((r) => {
        if (!alive) return;
        goodOwner.current = owner;
        setPreview(r);
      })
      /* A FAILED SWITCH MUST NOT BE A FAILED PAGE.
       *
       * `loadError` replaces the entire route with a bare message, which is
       * right for a first load that cannot happen — there is no page to show.
       * It is wrong for a switch: the consent screen the person was reading was
       * fine, and blanking it strands them with nothing to press and no way
       * back but a manual reload. (Clearing the error on the next owner change
       * does not help: once the page is gone there is no control left to change
       * it with.)
       *
       * So a switch that fails goes BACK to the owner that worked and says why.
       * Admin rights lapsing between the groups call and the press is a real
       * 403, and the honest answer is "you cannot grant for that one", not a
       * dead end. */
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || 'Could not load this request';
        if (goodOwner.current !== null && goodOwner.current !== owner) {
          setSwitchError(msg);
          setActor(goodOwner.current === 'me' ? null : goodOwner.current);
        } else {
          setLoadError(msg);
        }
      });
    return () => { alive = false; };
  }, [query, params, owner, setActor, seen, user?.id]);

  /* THE GROUPS THIS PERSON MAY GRANT FOR. Only an app edge can carry a group
   * owner, so nothing else pays for the call. A failure is silence, not an
   * error: the page's job is the grant in front of it, and losing the option to
   * switch must never stop someone authorizing for themselves. */
  const [myGroups, setMyGroups] = useState(null);
  // Not on a runtime ask either: the batch names the grant it widens, so there
  // is no owner to choose.
  const wantsActors = !!query.clientId && !query.subEntityKey && !query.editAuthId
    && !query.requestId;
  useEffect(() => {
    // no session, nothing to ask (and a 401 here would log the visitor out
    // from under the sign-in they are being sent to)
    if (!wantsActors || !user?.id) return undefined;
    let alive = true;
    // Started inside a resolved promise so that EVERY failure lands in one
    // place — a network error, a 403, and an api client that does not carry the
    // method all degrade to "no groups you administer", which renders the page
    // exactly as it is today rather than an error where a consent screen was.
    Promise.resolve()
      .then(() => api.getGroups())
      .then((r) => {
        if (alive) setMyGroups(r?.groups || (Array.isArray(r) ? r : []));
      })
      .catch(() => { if (alive) setMyGroups([]); });
    return () => { alive = false; };
  }, [wantsActors, user?.id]);

  const actors = useMemo(
    () => actorsFor({ username: user?.username, groups: myGroups || [] }),
    [user, myGroups],
  );

  const grant = useGrant(preview);

  // ── WHO ELSE GAINS, ASKED BEFORE THE BUTTON AND NOT AFTER ──────────────────
  //
  // Growing a realm grows every grant attached to it that is unrestricted on the
  // same verbs, with no re-authorization anywhere. The page used to say so in the
  // abstract — "this has sharing consequences" — which is the kind of sentence a
  // reader skips because it could be true of anything. `preview-widening` answers
  // it with names, and names are what make this a decision rather than a dare.
  //
  // Asked when the option is SELECTED, not when the button is pressed: selecting
  // is not committing, and the whole point is to be able to change your mind on
  // what comes back. Cleared the moment the answer stops applying, so a stale
  // list can never sit under a different realm's name.
  const widenPickId = (grant.placeIn && grant.placeIn !== 'none' && grant.placeIn !== 'shared')
    ? String(grant.placeIn) : null;
  const [widenImpact, setWidenImpact] = useState(null);
  useEffect(() => {
    if (!widenPickId) { setWidenImpact(null); return undefined; }
    let live = true;
    const base = query.groupId ? `/groups/${query.groupId}/privacy-realms` : '/privacy-realms';
    setWidenImpact(null);
    api.request(`${base}/${widenPickId}/preview-widening`, {
      method: 'POST',
      body: {
        access: grant.homelessAccess,
        // The grant being authorized is not "somebody else who gains" — it is
        // the point. Without this the page warns you about yourself.
        ...(preview?.existing?.ep_id ? { exclude_ep_id: preview.existing.ep_id } : {}),
      },
    })
      .then((r) => {
        if (live) setWidenImpact({ gaining: r?.gaining || [],
                                   unbounding: r?.unbounding || [] });
      })
      .catch(() => { if (live) setWidenImpact({ failed: true }); });
    return () => { live = false; };
  }, [widenPickId, grant.homelessAccess, query.groupId, preview?.existing]);

  // WHICH VERBS THIS REALM WOULD STOP NAMING TYPES FOR — THE SERVER'S ANSWER, NOT
  // THE PAGE'S.
  //
  // The page can see that the ask carries no schema id. It cannot see whether the
  // realm is ALREADY unrestricted on that verb, which is the difference between
  // a limit coming off and nothing happening — `_would_unbound` names only what
  // would change. An earlier draft approximated it with the preview's
  // `unrestricted` flag, which means "any nodes verb grants every schema": on a
  // realm wide for `nodes:read` and narrow for `activity:read` it would have
  // hidden the warning while the submit still asked for the widening. One fact,
  // one place, and the place is the route that acts on it.
  const unbounding = widenImpact?.unbounding || [];

  // MAKING A REALM UNRESTRICTED IS ALLOWED ONLY ONCE THIS PAGE HAS SHOWN WHAT
  // THAT MEANS, and the route enforces it by refusing without the key. So the key
  // is set from the thing itself — the same list the warning is rendered from —
  // and not from an intention to have fetched one. A failed or pending fetch
  // sends nothing, the route answers WIDEN_UNBOUNDED, and the person is told the
  // realm is too narrow rather than quietly handed the widest version of it.
  const widenImpactReady = !!widenImpact && !widenImpact.failed;

  /** AND THE PAGE'S OWN READING OF THE ASK, WHICH GATES A WAIT AND NOTHING ELSE.
   *
   *  `homelessUnbounded` is derived from the ask rows, so it can be wrong about
   *  the realm — it says the request has no type limit, not that this realm would
   *  lose one. That makes it useless for the warning and fine for this: while it
   *  says the question might arise and the answer has not landed, the button
   *  waits. Over-waiting costs a moment; submitting early would have sent no key
   *  and bounced off WIDEN_UNBOUNDED, showing a refusal for a grant the person
   *  had already been told they could make.
   *
   *  An approximation may gate a convenience. It may not gate the grant.
   */
  const awaitingWidenAnswer = !!widenPickId && grant.homelessUnbounded && !widenImpact;

  const { expiry, setExpiry } = grant;

  const edge = useMemo(() => {
    if (!preview) return null;
    try {
      return edgeFor({
        kind: preview.requester?.kind,
        actingAsGroup: !!preview.acting_as,
        nodeId,
        runtime: !!preview.requester?.runtime,
      });
    } catch (e) {
      setLoadError(e.message);
      return null;
    }
  }, [preview, nodeId]);

  /** The delegated decryption key, or nothing when it does not apply. */
  const wrapFor = useCallback(async (keys) => {
    const pub = preview?.requester?.enc_public_key;
    if (!pub || !keys?.privateKey) return null;
    try {
      return await wrapPrivateKeyForApp(keys.privateKey, pub);
    } catch {
      return null;       // fall through to the server's retry prompt
    }
  }, [preview]);

  /**
   * THE APP'S CALLBACK URL, not the bare redirect_uri.
   *
   * An app does not learn anything from being sent back to its own address. It
   * reads `code=success` to know consent happened, `user_id` to know whose, and
   * swaps the single-use `sid_code` server-side for a Paradigm session so a
   * later logout is centrally enforceable. ink, missions, story and entropy all
   * gate on `code === 'success'` and redirect to `?error=auth_failed` without it.
   *
   * The old page built this; the rebuilt one sent `redirect_uri` verbatim, so
   * every app's login failed after an otherwise successful grant.
   */
  // Shared with the paid return page, which has to hand back identically — see
  // components/grant/appCallback.js for what went wrong when it did not.
  // Set once a first account's first app is being approved (firstTime, below):
  // the hand-back then says the person has just joined.
  const firstApproved = useRef(false);
  const appCallbackUrl = useCallback(async () => buildAppCallbackUrl(
    redirectUri, preview?.requester?.client_id || query.clientId || '', user,
    { joined: firstApproved.current },
  ), [redirectUri, preview, query.clientId, user]);

  /**
   * A RE-AUTHORIZATION NOBODY REQUIRES IS NOT A QUESTION.
   *
   * `existing.reauth_required === false` is compute_reauth_status' answer — the
   * same one behind /third-party/me's `reauth` block and the ep.* webhooks. It
   * means the grant is live, unpaused, and covers the app's declared DLR under
   * the semantics ENFORCEMENT uses. Approving from here writes consent the app
   * effectively already holds, so the page asks a question whose only available
   * answer changes nothing.
   *
   * Keyed on that rather than on `state`, deliberately. `state` diffs the ask
   * against the RAW consent blob, where a resource with no entry reads as "not
   * granted"; enforcement reads the same absence as "inherit the realm". The two
   * disagree on four apps in thirteen live — Ink among them — and keying the skip
   * on `state` would keep showing a card for a grant every other part of the
   * platform considers complete.
   *
   * `state` is still consulted for LIVENESS, which reauth_required does not cover:
   * a revoked, paused or expired grant must reach the card however satisfied it
   * looks. Those three are exactly the rungs above 'expand' in preview's ladder.
   *
   * The guards are otherwise deliberately narrow, because the cost of being wrong
   * is a grant nobody was shown. Only an APP edge, only when there is a callback
   * to return through, and never when the visit carries an intent of its own —
   * editing the grant (`edit_auth_id`), a group or sub-entity scope, one note
   * (`node_id`), a follow or a request, or acting for a group. Payment is excluded
   * too: entitlement is settled at /authorize/approve, and there is no silent way
   * to take someone's money.
   *
   * Nothing is written. The redirect mints a session and hands back the same
   * `code=success&user_id&sid_code` the approve path builds, so an app cannot
   * tell this from a person clicking the button — which is the point, since a
   * person clicking the button would have changed nothing either.
   */
  /* Set only when the hand-back itself failed, which puts the card back. */
  const [handBackFailed, setHandBackFailed] = useState(false);

  // WAIT FOR THE IDENTITY, because the callback's whole job is to carry it.
  //
  // buildAppCallbackUrl spells a missing user as `user_id=` rather than failing,
  // and every app reads that as no user at all — missions answers
  // `?error=missing_user_id`. The approve path never meets this because a person
  // takes seconds to click and AuthProvider's fetchUser has long since landed;
  // removing the person removes that delay too. `user` hydrates synchronously
  // only from the cached snapshot, so a cold boot — which is exactly what a
  // private window is — starts null and fills in asynchronously.
  //
  // Not a failure when absent, just not yet: `user` arriving flips this and the
  // effect below runs then.
  const nothingToAsk = useMemo(() => Boolean(
    !handBackFailed
    && preview
    && user?.id
    && preview.requester?.kind === 'app'
    && preview.existing?.reauth_required === false
    && ['active', 'expand'].includes(preview.existing?.state)
    && redirectUri
    && preview.requester?.payment_required !== true
    && !preview.acting_as
    && !query.groupId && !query.subEntityKey && !query.editAuthId
    && !query.followId && !query.requestId && !query.shareToRealm
    && !query.shareToGroup && !nodeId,
  ), [handBackFailed, preview, user, redirectUri, query, nodeId]);

  // A REF, NOT STATE, AND NO CLEANUP — both deliberate.
  //
  // Re-entrance has to be guarded without changing the effect's dependencies:
  // flipping a state flag re-runs this effect, and the previous run's cleanup
  // then cancels the navigation that is still in flight. That left the page
  // sitting on an empty frame forever, and it reproduced only under some
  // orderings because it is a race between the promise and React's re-render.
  //
  // Nor is the navigation cancellable on unmount. Once the decision to hand back
  // is made the person is leaving this page; aborting it on the way out is how a
  // hand-back gets dropped rather than how one gets tidied up.
  const handedBack = useRef(false);
  useEffect(() => {
    if (!nothingToAsk || handedBack.current) return;
    handedBack.current = true;
    appCallbackUrl()
      .then((url) => { window.location.href = url; })
      // The card, rather than a dead end: a session that could not be minted is
      // a reason to ask the question the normal way, not to strand the person.
      .catch(() => { handedBack.current = false; setHandBackFailed(true); });
  }, [nothingToAsk, appCallbackUrl]);

  const onApprove = useCallback(async (keysOverride) => {
    const keys = keysOverride || encKeys;
    setSubmitError(null);
    setSubmitViolations(null);
    setSubmitting(true);
    try {
      // The chosen relationship kinds are stamped on HERE, at submit — see
      // `applyPickedTypes`. The displayed ask stays server state; only what is
      // sent carries the refinement.
      // WITH NO ASK, THE REALM IS THE GRANT — and it has to reach the wire, not
      // just the screen. `ask.grants` is empty for a friend (no DLR), so building
      // consent from it sent `{}`, which is deny-all: a Full Access share stored
      // NO NODE ACCESS and the friend saw nothing.
      //
      // Scoped to `user`, and to realm shares only, on purpose:
      //   - `follow` reads `grants.length` as a boolean, so filling it here would
      //     silently turn accepting a request into sharing;
      //   - `group`/`public` send no consent at all;
      //   - a one-note share (`isAct`) must stay scoped to that note — widening
      //     it to everything the realm reaches is the one direction this must
      //     never be wrong in, so it sends no consent and the server applies its
      //     node-scoped read-only default.
      // THE ROWS THE REALMS REACH, PLUS THE ROWS BEING PLACED.
      //
      // `filter(isOn)` alone is what made Authorize a quiet narrowing: a line no
      // picked realm covered was dropped here, the request succeeded, and the app
      // held a grant missing what it asked for with nothing having said so. The
      // placed rows are the ones the person has just chosen a home for, so they
      // belong in the consent that home is being opened for. Declining places
      // nothing, which is the old behaviour — now because someone picked it.
      const on = grant.hasAsk
        // `isChecked` IS the realm's rows plus the placed ones — the same test the
        // page marks with. Concatenating the two lists separately said the same
        // thing twice and let the screen and the wire drift apart.
        ? grant.applyPickedTypes((preview.ask.grants || []).filter(grant.isChecked))
        : (preview?.requester?.kind === 'user' && !grant.isAct
          ? grantsFromRealms(grant.realms)
          : []);
      // WHERE THIS GOES DEPENDS ON WHO IS RECEIVING IT. A share is not an
      // authorization is not an invitation reply — this used to post everything
      // to /authorize/approve, which meant every edge but `app` threw before it
      // sent anything.
      const plan = submitPlan({
        preview,
        realmIds: grant.realmIds,
        // Sent only when this screen settled it; omitted, the relationship resolves
        // the face the usual way (the realms, then your default).
        faceId: grant.faceId,
        grants: on,
        // ONE ANSWER, FOR EVERY VERB. The control is asked once now, so there
        // is no per-tier lookup to do — and the lookup was always a fiction:
        // `scope_type` is stored per VERB across every schema it touches, so
        // two tiers sharing a verb could hold two answers for one stored value.
        scopeFor: () => grant.scope.all || 'any',
        // THE SHAPE OF THE GRAPH, when it was asked for and a chosen realm
        // carries it. It has no ask row to ride in on `grants` — it is a key on
        // the read scope — so a blob built from the rows alone always said no,
        // whatever the page had shown. Sent only when both sides hold it,
        // because that is the AND enforcement applies anyway.
        links: grant.linksOn,
        // `keep` SENDS THE DATE BACK, EXPLICITLY. Sending null made
        // buildApproveBody omit the key, and /authorize/approve is a total
        // re-statement of the grant — an absent expires_at is assigned as
        // None. So "keep the current date" silently CLEARED the term to
        // never-expires, which is the one direction this page must not be
        // wrong in. C10 only offers `keep` for a future date, so this can
        // never send back a lapsed one.
        expiresAt: expiry === 'keep'
          ? (preview.existing?.expires_at || null)
          : expiresAtFrom(expiry),
        groupId: query.groupId,
        redirectUri,
        toYaml: accessBlobToYaml,
        // WHERE THE LEFTOVERS GO. `shared` mints (or extends) the realm this app
        // alone is in — the narrowest home, holding exactly what was asked. A
        // realm id GROWS that realm instead, which is why the control warns
        // before it is chosen. Omitted when nothing is being placed, so a grant
        // that fits its realms sends exactly what it always did.
        placement: grant.placedGrants.length
          ? (grant.placeIn === 'shared'
            ? { mintSharedRealm: true }
            // AND, WHEN THE RESIDUE HAS NO TYPE LIMIT, THE WORD THAT LETS IT
            // THROUGH — set from an impact answer in hand, never from the mere
            // intention to fetch one. See `widenImpactReady`.
            : { widenRealmId: grant.placeIn,
                ...(widenImpactReady && unbounding.length
                  ? { widenUnbounded: true } : {}) })
          : null,
        // An encrypting app needs the owner's key wrapped to ITS public key.
        // Sent on the first try when the key is already unlocked, so the retry
        // prompt only appears for a session that has not entered a password.
        encUserPrivkey: await wrapFor(keys),
        extra: { invitationId: params.get('invitation_id') },
      });
      // a parked plugin run is answered through requestsApi, as its Decline is
      const client = plan.client === 'requests' ? requestsApi : api;
      if (typeof client[plan.call] !== 'function') {
        throw new Error(`This kind of grant cannot be saved yet (${plan.call}).`);
      }

      // ── PAYMENT FIRST, for an app that needs paying for ──────────────────
      //
      // The decision is made ONCE, here. We send what they chose to the checkout,
      // which holds it while they are at Stripe, and the return page completes the
      // grant they already decided on — rather than making them choose a realm, pay,
      // and then choose a realm again.
      //
      // NO GRANT IS CREATED BEFORE PAYMENT. An abandoned checkout must not leave
      // standing access to somebody's identity graph, so the failure mode of a payment
      // problem is "no access" and never "unpaid access".
      if (preview.requester?.payment_required) {
        const body = plan.args[0] || {};
        const res = await api.startCheckout({
          client_id: preview.requester.client_id,
          privacy_realm_id: grant.realmIds?.[0],
          group_id: query.groupId || undefined,
          // Which period they chose. Omitted before, so the backend fell back to the
          // first available and every buyer paid monthly.
          interval: interval ?? firstInterval ?? undefined,
          // What they decided, carried through Stripe on the purchase row.
          consent: body.consent_yaml ? { consent_yaml: body.consent_yaml } : undefined,
          success_url: `${window.location.origin}/grant/paid?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: window.location.href,
        });
        // Already theirs — a live purchase existed after all, so fall through and
        // authorize rather than charging twice.
        if (!res?.already_purchased) {
          // Remember the exact approve payload, so the return page finishes this grant
          // and not a default one. sessionStorage survives the Stripe round trip in the
          // same tab; if it does not, /grant/paid falls back to the consent screen,
          // which will then show the app as already paid for.
          sessionStorage.setItem('paradigm.pendingGrant', JSON.stringify({
            call: plan.call, args: plan.args, redirectUri,
            clientId: preview.requester.client_id,
          }));
          window.location.href = res.checkout_url;
          return;
        }
      }

      await client[plan.call](...plan.args);
      // A RUNTIME ASK GOES BACK TO WHERE THE PERSON WAS. The app is mid-flow —
      // Missions is on `/signals/connected?state=…`, Ink on its share screen —
      // already signed in, holding its own state in the URL it gave us. It is
      // not logging in, so the `code=success&sid_code` handshake the first
      // grant builds has nothing to carry here, and the old consent page
      // never sent it: every app's return route was written against the bare
      // redirect_uri. Handed back exactly as given.
      if (redirectUri && preview.requester?.runtime) window.location.href = redirectUri;
      else if (redirectUri) window.location.href = await appCallbackUrl();
      // Joining a group lands you IN it — not on a list of the apps you've authorized.
      else if (preview?.requester?.kind === 'group' && preview.requester.id) {
        navigate(`/group/${preview.requester.id}/graph`);
      }
      else navigate('/authorized-apps?granted=1');
    } catch (e) {
      // A RETRY, NOT A FAILURE. The server rolled the grant back and asked for
      // the key; showing its sentence as a flat error left the reader holding
      // an instruction the page refused to let them carry out.
      if (e?.code === 'ENCRYPTION_KEY_REQUIRED') {
        setNeedsKey(true);
        setSubmitting(false);
        return;
      }
      setSubmitError(e?.message || 'Could not save this grant');
      setSubmitViolations(Array.isArray(e?.violations) && e.violations.length
        ? e.violations : null);
      setSubmitting(false);
    }
  }, [preview, grant, expiry, query.groupId, redirectUri, navigate, params,
      encKeys, wrapFor, appCallbackUrl, widenImpactReady]);

  const onUnlock = useCallback(async (password) => {
    setKeyError(null);
    setSubmitting(true);
    const keys = await unlockEncKeys(password);
    if (!keys) {
      setKeyError('That password did not unlock your key. Try again.');
      setSubmitting(false);
      return;
    }
    setNeedsKey(false);
    setSubmitting(false);
    onApprove(keys);                   // same grant, now with a key to delegate
  }, [unlockEncKeys, onApprove]);

  /**
   * A FIRST ACCOUNT'S FIRST APP ASKS NOTHING (owner call 2026-10-04).
   *
   * Someone who has just joined ofSelf from an app (pages/join) holds no nodes:
   * there is nothing yet for a consent screen to weigh, and asking them to pick
   * realms over an empty graph is a question with no content. So the app they
   * came from gets Full Access — their default realm — with no expiry, through
   * the very approve path the button takes (onApprove), and they land in it.
   *
   * Only for the tab that created the account (joinFlow's `fresh` mark, cleared
   * on the first use), only for a plain app sign-in — never a runtime ask, a
   * paid app, a group, a share or anything acting for someone else — and only
   * while it is working: a failed approve or a key prompt puts the card back.
   */
  const fullAccess = useMemo(() => (preview?.realms || [])
    .find((r) => r.is_default && r.fits && !r.is_shared_realm) || null, [preview]);
  // Once approving, it stays held (firstApproved): the mark is spent at the
  // first use, and the card must not paint while the browser is on its way.
  // `firstDeclined`: Full Access is chosen but cannot confirm (a default realm
  // that reaches nothing), so the shortcut steps aside for the card.
  const [firstDeclined, setFirstDeclined] = useState(false);
  const firstTime = Boolean(
    preview && user?.id && !firstDeclined && (firstApproved.current || isFresh(user.id))
    && preview.requester?.kind === 'app'
    && !preview.existing
    && redirectUri
    && !preview.requester?.runtime
    && preview.requester?.payment_required !== true
    && !preview.acting_as
    && !query.groupId && !query.subEntityKey && !query.editAuthId
    && !query.followId && !query.requestId && !query.shareToRealm
    && !query.shareToGroup && !nodeId
    && fullAccess && !grant.isRealmLocked
    && !submitError && !needsKey,
  );
  useEffect(() => {
    if (!firstTime || firstApproved.current) return;
    if (!grant.realmIds.includes(fullAccess.id)) { grant.setRealmId(fullAccess.id); return; }
    if (grant.expiry !== 'never') { grant.setExpiry('never'); return; }
    // canConfirm is derived in render, so with the realm and expiry settled a
    // false here is final: hand over to the consent card rather than hang.
    if (!grant.canConfirm) { clearFresh(); setFirstDeclined(true); return; }
    firstApproved.current = true;
    clearFresh();
    onApprove();
  }, [firstTime, fullAccess, grant, onApprove]);

  // SIGNED OUT GOES STRAIGHT TO SIGN-IN. The consent page mounts for anyone
  // (an app links here), and it used to paint its own loading frame, ask for
  // the preview, take the 401, and only then reload into /login: a frame of
  // consent chrome nobody could use, then a full reload. With no session it
  // now hands over at once, in the app, carrying the whole address as `next`.
  if (!authLoading && !user) {
    return <Navigate to={loginUrlFor(location)} replace />;
  }
  if (loadError) {
    return (
      <GrantFrame className="gp-route">
        <div className="gp-route--error" role="alert">{loadError}</div>
      </GrantFrame>
    );
  }
  if (!preview || !edge) {
    return <GrantFrame className="gp-route" data-testid="loading" aria-busy="true" />;
  }
  // Held BEFORE the card is built, not hidden after. `nothingToAsk` is computed
  // during render, so a grant that asks nothing never paints a consent screen
  // for the frame it takes the effect above to navigate.
  if (nothingToAsk || firstTime) {
    return <GrantFrame className="gp-route" data-testid="handing-back" aria-busy="true" />;
  }

  // SECTION NUMBERS ARE GONE. They were positions rather than labels — computed
  // from the edge's sequence, because hardcoding "01" on the ask printed 02
  // above 01 on every no-DLR edge. The machinery was right and is now moot: the
  // headings are instructions ("Add privacy realms to fulfill those
  // requirements"), and an instruction does not need an index. What the numbers
  // guarded — that the ask precedes the realm on an app edge — is a property of
  // `sequenceFor`, which still holds it and is still tested.

  const who = preview.requester;
  /* A GROUP EDGE IS ONE OF THREE MOMENTS, and the address says which: answering
     an invitation (`invitation_id`), finishing a group you just made (`made`), or
     changing what you share with one you are in. Each is the same two contracts
     — what the group lets you do, read only, and what you share, chosen here —
     so creator and invitee leave this page holding the same thing. */
  const standing = who.standing || null;
  // `made` is only the creator's step when the server says you are its admin: the
  // address alone would let any member read "you are its first admin".
  const groupStep = who.kind !== 'group' ? null
    : params.get('invitation_id') ? 'join'
      : params.get('made') && standing?.role === 'admin' ? 'made' : 'edit';
  // A contract on your null realm shares nothing, so it names no realm here.
  const sharedNames = (preview.existing?.realm_ids || [])
    .map((id) => (preview.realms || []).find((r) => String(r.id) === String(id) && !r.is_null_realm)?.name)
    .filter(Boolean);
  const groupHeadline = groupStep === 'made'
    ? <><b>{who.name}</b> is ready. What do you share with it?</>
    : groupStep === 'join'
      ? <>Join <b>{who.name}</b>.</>
      : <>Change what you share with <b>{who.name}</b>.</>;
  const asRole = (role) => (role === 'admin' ? 'an admin' : 'a member');
  const groupSub = groupStep === 'made'
    ? `You made ${who.name}, so you are its first admin, on Full Access.`
    : groupStep === 'join'
      ? (standing?.invitation
        ? `${standing.invitation.invited_by_name || 'Someone'} invited you as ${asRole(standing.role)}${standing.invitation.created_at ? `, ${dateOf(standing.invitation.created_at)}` : ''}.`
        : null)
      : (sharedNames.length && preview.existing?.created_at
        ? `You have shared ${sharedNames.join(', ')} since ${dateOf(preview.existing.created_at)}.`
        : 'You share nothing with it now. The contract stays, empty, until you add a realm.');
  /** Does the headline already name this party? Then nothing below it should.
   *  `sub` is decided separately: on a realm edge the strip is gone but the line
   *  saying WHICH APP asked for the share is not a restatement of anything — it
   *  is the only place that party is named. */
  // A PERSON TOO. "Choose what dark can see" names them, and the strip under it
  // said "dark" again beside a square of their initials — the restatement the
  // group edge already dropped, left standing on the share it most resembles.
  // PUBLIC AND SUB-ENTITY TOO, the last two the list had not caught up with.
  // "Choose what Anyone on the internet can see" was followed by a square
  // reading "An" and the same six words again; "scout wants to act under
  // Personas" by a square reading "sc", the name once more, and "An agent inside
  // Personas" — which is the headline rewritten. The rule was never about which
  // kinds: it is that a headline naming the party leaves nothing for a strip
  // below it to add, and every kind this page renders now does that.
  const hideWho = who.kind === 'realm' || who.kind === 'app'
    || who.kind === 'group' || who.kind === 'group_share'
    || who.kind === 'user' || who.kind === 'follow'
    || who.kind === 'public' || who.kind === 'sub_entity'
    || !!grant.item;
  // A node share to a REALM keeps its sub-line: there it names the app that
  // asked, which appears nowhere else. A node share to a PERSON does not — the
  // line there is that person's own description, under a headline naming them.
  // A SUB-ENTITY'S BLURB IS ITS HEADLINE. "An agent inside Personas" under
  // "scout wants to act under Personas" is the same sentence twice.
  //
  // THE PUBLIC EDGE KEEPS ITS OWN. "No account needed. Indexable by search." is
  // not a restatement — it is the only place the page says what `public`
  // actually means, and it is the consequence the reader is deciding about. With
  // the strip's name and tile gone it stands alone under the headline, which is
  // where that sentence wanted to be.
  const hideBlurb = who.kind === 'app' || who.kind === 'group'
    || who.kind === 'user' || who.kind === 'follow' || who.kind === 'sub_entity'
    || (!!grant.item && who.kind !== 'realm');
  // The ask's coverage, computed once: the verdict states it and the receipt
  // waits on it, and the two must not be able to disagree.
  const askTotal = (preview.ask?.grants || []).length;
  const askMet = (preview.ask?.grants || []).filter(grant.isChecked).length;
  const askedVerbs = [...new Set((preview.ask.grants || []).map((g) => g.verb))];
  // The same call the submit makes, off the same checked rows, so the sentence about
  // the share and the share cannot disagree (components/grant/consent.js).
  const groupShareAccess = nodeShareAccess(
    (preview.ask?.grants || []).filter(grant.isChecked));
  // A sub-entity is not a thing on its own — it acts UNDER an app, and the app
  // is what you actually authorized. Naming only the agent hid the party whose
  // grant it is borrowing.
  // WHOSE IDENTITY, SAID AT FULL SIZE. On a group edge "your identity" is the
  // product's generic voice, but one line below it the page says "your own data
  // isn't involved" — the same word in two senses, and the reader resolves it
  // only by reading the smaller, greyer line. That is the wrong place for the
  // personal/group distinction to live: on this edge you are authorizing an app
  // against OTHER PEOPLE's graphs, and someone who skims the headline and
  // accepts believes they exposed themselves.
  //
  // Only the ask branch can carry it. The realm, sub-entity and no-ask headlines
  // are sentences about something else, so on those edges the name stays where
  // it was — see `namesActor` below, which is what keeps C2 from dropping a
  // fact nothing else states.
  const actingName = grant.actingAs?.name;
  const namesActor = !!actingName && grant.hasAsk
    && who.kind !== 'realm' && who.kind !== 'sub_entity';
  /* A RUNTIME ASK SAYS "MORE", because the first grant already said "part".
     "Missions is asking for part of your identity" is the sentence for an app
     you have not connected; read again over a grant you hold, it implies the
     page is starting over. The person is being asked to add to something, and
     the headline should say which something. */
  const headline = who.kind === 'realm'
    ? <>File <b>{preview.item?.title || 'this note'}</b> into a realm?</>
    : who.kind === 'group_share'
    ? <>Share <b>{preview.item?.title || 'this note'}</b> with <b>{who.name}</b>?</>
    : who.kind === 'sub_entity'
    ? <><b>{who.name}</b> wants to act under <b>{who.parent_app_name}</b>.</>
    : who.runtime
    ? (namesActor
      ? <><b>{who.name}</b> is asking for a bit more of <b>{actingName}</b>&rsquo;s identity.</>
      : <><b>{who.name}</b> is asking for a bit more of your identity.</>)
    : grant.hasAsk
      ? (namesActor
        ? <><b>{who.name}</b> is asking for part of <b>{actingName}</b>&rsquo;s identity.</>
        : <><b>{who.name}</b> is asking for part of your identity.</>)
      /* THE INTENT, NOT THE MECHANISM. A group or a person arrives here because
         you are joining, befriending, or already sharing with them — and that is
         what the page should say first, not "choose what they can see", which is
         the step below it. The preview carries no membership or friendship flag,
         so a grant that already exists is the signal that you are editing one. */
      : who.kind === 'group'
        ? groupHeadline
      : preview.existing && who.kind === 'user'
        ? <>You&rsquo;re sharing with <b>{who.name}</b>.</>
        : who.kind === 'group'
          ? <>You want to join <b>{who.name}</b>.</>
          : who.kind === 'user'
            ? <>You want to be friends with <b>{who.name}</b>.</>
            : <>Choose what <b>{who.name}</b> can see.</>;

  // A BUTTON THAT TAKES YOU TO A CHECKOUT MUST SAY SO. "Authorize" on a paid app
  // understates what pressing it does — the same reason the store card says "Get" — and
  // this is the moment of commitment, so it matters more here. The amount is already on
  // the page above, so the label only has to be honest about where the click leads.
  //
  // "Redirecting", not "Saving", while leaving: nothing is saved until they come back
  // paid, and saying otherwise would imply a grant that does not exist yet.
  const needsPayment = Boolean(who.payment_required);
  // Which billing period they picked. Defaults to the first the app offers, which is
  // what the backend would have chosen anyway — the difference is that it can now be
  // CHANGED, so the annual saving the card advertises is actually takeable.
  const firstInterval = who.pricing_display?.intervals?.[0]?.interval || null;
  if (interval === undefined && firstInterval) setInterval(firstInterval);
  // An expiry shorter than the billing period would stop access while the charges
  // continued — the person paying for a month and time-limiting themselves to a week,
  // from a control that mentions no money. Only the coherent choices are offered.
  const expiryOptions = expiryOptionsFor(who.pricing_display, interval ?? firstInterval);
  // ONE PAIR OF BUTTONS ON EVERY EDGE: Deny and Authorize. A one-node share said
  // "Cancel / Share it" and a group join says "Deny / Authorize" — the same
  // decision, in two vocabularies, on pages a person meets minutes apart. And
  // "Cancel" is the weaker word of the two: it reads as abandoning a form rather
  // than refusing a request, which is what the button actually does.
  //
  // The exception stays, because it is not a rename but a different act: a button
  // that opens a checkout has to say where the click leads.
  // THE BUTTON STATES THE GRANT IT MAKES.
  //
  // Leaving permissions out is a legitimate answer — it is the whole point of
  // being able to grant an app less than it asked for — so the button stays live
  // for it. What it must not do is read "Authorize" while making a narrower grant
  // than the app asked for: the × rows above say what is short, and then the
  // control at the moment of commitment said nothing about it.
  //
  // It mirrors the verdict's own words, so the two agree at a glance: "Covers
  // 3 of 4" above, "Authorize 3 of 4" at the foot.
  // Only once the button can actually do something. With no realm picked it is
  // dead and the count is not a fact about a grant, it is "0 of 4" on a control
  // that cannot be pressed.
  const partial = grant.hasAsk && grant.canConfirm && askTotal > 0 && askMet < askTotal;
  const confirmLabel = submitting
    ? (needsPayment ? 'Redirecting\u2026' : 'Saving\u2026')
    : needsPayment
      ? 'Continue to payment'
      : groupStep === 'join' ? `Join ${who.name}`
        : groupStep === 'made' ? (grant.realmIds.length ? 'Share and go in' : 'Share nothing and go in')
          : groupStep === 'edit' ? 'Save'
            : partial ? `Authorize ${askMet} of ${askTotal}` : 'Authorize';

  /* Does this app ask to READ identities? The face only reaches an app that asked
     for the `profile` schema, so the section says which of the two screens this is. */
  /* Did the app NAME `profile`, or merely sweep it up? Naming it is a question about
     who you are, so the section asks one-or-all; a wildcard read asked nothing, and the
     face is shown without a question. The server decides this — breadth is not an ask. */
  const askedForIdentity = !!preview?.ask?.asks_identity;

  // WHAT OF EACH SCHEMA THE CHOSEN REALMS READ, by name, in the words the realm and
  // contract panels use; Boundary and Receipt put them under the row they qualify.
  const readNotesByName = (() => {
    const chosen = (preview?.realms || []).filter((r) => (grant.realmIds || []).map(String).includes(String(r.id)));
    const bySid = readNotes(chosen);
    const out = {};
    for (const row of grant.inventoryPlus || []) if (bySid[String(row.schema_id)]) out[row.schema_name] = bySid[String(row.schema_id)];
    return out;
  })();

  // an app's own grant, opened from its Edit access (the same EP is overwritten)
  const editingApp = !!query.editAuthId && who.kind === 'app' && !who.runtime;

  const slots = {
    // PRICE — WHAT IT COSTS, after the app is named and before the ask.
    //
    // On the same card as the DLR, and first, because "what does it take" and "what
    // does it cost" are one decision made at one moment: there is no point choosing
    // which beliefs to expose to an app you are not going to pay for.
    //
    // The strings come pre-rendered from the server (`pricing_display`) — nothing here
    // formats money. A free app renders nothing at all, so the line appearing means
    // something.
    // THE HEADING SITS OUTSIDE THE BOX, because it names the whole box — the
    // same relation "WHAT MISSIONS REQUIRES" has to the list below it. Inside,
    // it was a third register of heading on a page that already had two, and it
    // read as part of the price rather than as the thing introducing it.
    // (The TIER labels stay inside their box: they name a group of rows, not the
    // box, and one box can hold several of them.)
    // NO HEADING. Cost and expiry are the same kind of thing — terms of the
    // grant — and a section title over one of them made two costumes for one
    // kind of fact. As a labelled pair the label IS the heading, and the two
    // read as one block: what it costs, how long it lasts, then the buttons.
    PRICE: <PriceLine display={who.pricing_display}
                      interval={interval ?? firstInterval}
                      onIntervalChange={setInterval}
                      alreadyPaid={who.payment_required === false
                                   && who.pricing_display?.is_paid} />,
    // THE APP EDGE SHOWS NO WHO-ROW. The headline one line above already names
    // the party — "Missions is asking for part of your identity" — so the tile,
    // the name, the developer's blurb and the headcount restated it beside an
    // avatar, above the only thing on this page anyone has to decide.
    //
    // ONLY the app edge. The row is load-bearing elsewhere and removing it
    // wholesale broke three edges: a GROUP join needs the member count (it is
    // the whole point of joining), a realm edge needs "Asked for by <app>", and
    // a sub-entity needs the parent app it acts inside. Those facts are stated
    // nowhere else on the page.
    //
    // The headcount was the app edge's one unique fact, and it goes on purpose:
    // it is social proof, not a term of the grant, and it argued FOR granting at
    // the top of a page whose job is to let someone decline.
    // WHERE THE HEADLINE ALREADY NAMES THE PARTY, THE STRIP GOES.
    //
    // "Choose what Research Team can see" — then an avatar, the same name again,
    // the member count and the group's own blurb, in the heaviest object on the
    // page, restating the one thing that was never in doubt. Same on a one-node
    // share. The strip earns its place only on an edge whose headline does NOT
    // carry the name, which is what `hideWho` decides.
    //
    // The member count goes with it, and that is a real loss: "1 member" is the
    // difference between a name and a room you can picture. If it comes back it
    // belongs in the sentence, not in a tile — the group's SIZE is part of what
    // you are agreeing to, and a blurb the group wrote about itself is not.
    C1: <Explanation headline={headline}
                     tile={hideWho ? null : whoGlyph(who)}
                     name={hideWho ? null : who.name}
                     count={hideWho || who.kind === 'app' || !(who.user_count > 0)
                       ? null : peopleLine(who)}
                     sub={groupStep
                       ? groupSub
                       // EDIT ACCESS IS A FULL REVIEW, said before anything
                       // else: every row below is what it can do, and saving
                       // replaces the grant it holds, not adds to it.
                       : editingApp
                       ? `You’re reviewing everything ${who.name} can do. Saving replaces its current access.`
                       : hideBlurb
                       ? null
                       : (who.kind === 'realm'
                         ? (who.via_app ? `Asked for by ${who.via_app}` : null)
                         : (who.kind === 'group_share'
                         // THE CONSEQUENCE, and the only place this edge states
                         // it. The group is not a shelf: its members read the
                         // note. A headcount is the difference between a name
                         // and knowing who you just handed something to — and
                         // the second sentence is the one a reader actually
                         // wants, because the fear here is handing over more
                         // than the one note.
                         ? `${who.user_count === 1 ? 'Its 1 member' : `Its ${who.user_count ?? 0} members`} `
                           + `will be able to ${groupShareAccess === 'edit' ? 'read and edit' : 'read'} `
                           + 'this note. Nothing else of yours is shared.'
                         : (who.kind === 'sub_entity'
                              ? `An agent inside ${who.parent_app_name}`
                              : who.description)))} />,
    // AFTER the owner, not inside the headline: see CANONICAL's note on WHY.
    WHY: <Why why={grant.why} />,
    // BELOW the disclaimers, not between them. "480 others" is context; the
    // why-banner and the acting-as line change what is being asked.
    ADOPTION: (who.kind === 'app' || who.kind === 'sub_entity') ? (
      <Adoption>
        {/* "USERS", which is exactly what the number counts: preview.py takes a
            DISTINCT over ExposureProfileGrant.owner_id, and every owner_id is a
            users.id — including the row a GROUP holds its grants under. So this
            is accurate where "people" would not be: some of those users are
            groups. */}
        {who.user_count > 0
          ? <>{who.user_count === 1
              ? <><b>1</b> other user has</>
              : <><b>{fmt(who.user_count)}</b> other users have</>} connected this.</>
          : <>You would be the <b>first</b> to connect this.</>}
      </Adoption>
    ) : null,
    // ONE LINE, AND NO SECOND COPY OF THE NAME. Once the headline names the
    // group, repeating it here said the same proper noun twice a line apart and
    // wrapped the sentence onto two rows. Where the headline canNOT name it —
    // a realm, a sub-entity, an edge with no ask — the name stays, because
    // nothing else on the page states it.
    // C2 now OFFERS the choice as well as stating it, on the app edges where a
    // group could own the grant. Everywhere else — a realm, a sub-entity, an
    // edit — it is the sentence it has always been, because those edges have no
    // other actor to pick.
    // Never a choice on a runtime ask: the batch names the grant it widens.
    C2: (who.kind === 'app' && !who.runtime && hasChoice(actors))
      ? (
        <ActingChoice actors={actors} groupId={query.groupId} onPick={pickActor}
                      error={switchError} />
      )
      : grant.actingAs && (
        <ActingAs>
          You&rsquo;re granting this as <b>{grant.actingAs.role || 'a member'}</b>
          {namesActor ? null : <> of <b>{grant.actingAs.name}</b></>}
          {' '}— your own data isn&rsquo;t involved.
        </ActingAs>
      ),
    // C3 — the request, editable from the start. The realm below decides what
    // any of it yields; ticking here is intent, not a grant.
    // NO NUMBER, NO HINT. The heading IS the instruction now, and it names the
    // party rather than saying "it" — the who-row that used to carry the name
    // is gone from this edge. The hint explained ticks that the realm rows
    // below explain better, at the moment they become true.
    /* NOT RENDERED WHEN THERE IS NOTHING TO STATE. A group join carries no DLR,
       so this drew "What Research Team requires" over an empty section — a
       heading asserting a list, followed by nothing, immediately above the one
       section that does have content. `cond` on the edge is what decides
       presence, and it can only decide on what it is told. */
    /* TWO READINGS OF ONE ASK. Prose is what a person reads — a handful of
       sentences, each marked as the realms answer it; the table is every type
       and verb, with the refinements (the type filter, the scope question) that
       only make sense against it. Same grants underneath, so they cannot
       disagree. */
    /* NO "NEW" MARKS ON A RUNTIME ASK. The mark distinguishes rows an app
       added from rows you already agreed to; here every row is new by
       construction — the batch holds only what the grant does not cover —
       so marking each one says nothing the banner has not, and a word beside
       every line reads as a warning. */
    // WHAT THE GROUP LETS YOU DO — the membership's other contract, stated
    // before the one this page asks about. Read only: the role is the inviting
    // admin's choice (or yours, having made it), the realm an admin's to change.
    ROLE: standing && (
      <Section title={`What ${who.name} lets you do`} aside="read only">
        <GroupStanding
          lines={[
            standing.role === 'admin'
              ? 'Be its admin: invite and remove people, change its realms, contracts, apps and settings'
              : 'Be its member: you don’t run the group',
            ...realmLinesForYou(standing.realm, who.name),
          ]}
          note={standing.realm ? (
            standing.role === 'admin'
              ? `On the realm ${standing.realm.name}. Admins are always on Full Access.`
              : standing.invitation
                ? `On the realm ${standing.realm.name}. ${standing.invitation.invited_by_name || 'The admin'} chose it when inviting you; an admin can change it.`
                : `On the realm ${standing.realm.name}. An admin can change it.`
          ) : null}
        />
      </Section>
    ),
    C3: grant.hasAsk && <Section title={`What ${who.name} wants`}
      actions={(
        <div className="gp-seg" role="tablist" aria-label="How to show the request">
          {[['prose', 'Prose'], ['table', 'Table']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k}
                    onClick={() => setView(k)}>{label}</button>
          ))}
        </div>
      )}>
      {view === 'prose' ? (
        <>
          <AskProse grants={preview.ask.grants} items={preview.ask.items}
                    available={grant.isChecked}
                    decided={grant.realmIds.length > 0}
                    previewRealm={previewRealm}
                    owner={grant.actingAs ? "the group's" : 'your'}
                    asksLinks={grant.asksLinks} linksOn={grant.linksOn}
                    isNew={who.runtime ? null : grant.isNew}
                    faceOf={grant.faceOf} />
          <p className="gp-to-table">
            You can customize if required, in{' '}
            <button type="button" className="gp-link" onClick={() => setView('table')}>
              Table view
            </button>.
          </p>
        </>
      ) : (
      <Ask buckets={grant.buckets} editable isOn={grant.isChecked}
             available={grant.isChecked} onToggle={grant.toggle}
             onToggleMany={grant.toggleMany} counts={grant.countsBySchemaId}
             scope={grant.scope} onScope={grant.setScopeFor}
             items={preview.ask.items} askGrants={preview.ask.grants}
             pickedTypes={grant.pickedTypes} onToggleType={grant.toggleType}
             decided={grant.realmIds.length > 0}
             scopeWideLabel={grant.actingAs
               ? `Anything of ${grant.actingAs.name}'s`
               : 'Anything of yours'}
             appName={who.name}
             blockedBy={grant.blockedBy}
             isNew={who.runtime ? null : grant.isNew}
             faceOf={grant.faceOf}
             owner={grant.actingAs ? "the group's" : 'your'} />
      )}
    </Section>,
    // The number and the hint go with C3's. The union rule the hint stated
    // ("the app gets whatever any of them allows") is asserted here to
    // everyone, forever, before there is a result to apply it to — the realm
    // rows say it at the moment two are picked.
    // THE HEADING IS EDGE-AWARE. "…to fulfill those requirements" is the app
    // edge's sentence: it points back at the ask above it. A friend, follow or
    // public share states no requirements, so on those edges the phrase names
    // something that does not exist — the question there is which part of you
    // this reaches, which is what it asks.
    /* ONE NOUN FOR IT, ON EVERY EDGE. "Which part of you" was a good sentence
       and a different name for the thing the rest of the product — and the two
       neighbouring pages — call a realm. A person meeting a group join,
       an app grant and a node share in one session should not have to work out
       that all three are choosing the same kind of object. The app edge keeps
       its pointer back at the ask, because there the heading has a list above it
       to point AT. */
    C4: <Section tone="act"
                 title={who.kind === 'realm' ? 'Which realm'
                   : who.kind === 'group' ? `What you share with ${who.name}`
                   : (grant.hasAsk || who.kind === 'user'
                      || who.kind === 'follow')
                     ? `Share to ${who.name} from these realms`
                     : 'Which realms'}
                 // How many are in, as a live count — the panel's state at a glance.
                 aside={grant.realmIds.length ? `${grant.realmIds.length} added` : null}
                 // KEPT FOR THE REALM EDGE ONLY. The hint the design drops is
                 // the app edge's union boilerplate — asserted to everyone,
                 // forever, before there is a result to apply it to. A note
                 // share's hint is a different sentence doing a different job:
                 // it says nobody new is added, which nothing else on that edge
                 // states.
                 // THE RULE, SAID ONCE, WHERE THE CHOICE IS MADE: the app gets only
                 // what the realms cover, and nothing moves until one is added.
                 hint={who.kind === 'realm'
                   ? 'Whoever this realm already reaches will be able to read this note. '
                     + 'Nobody new is added.'
                   : who.kind === 'group'
                     ? `${who.name}’s members only reach what these realms cover, from your own graph.`
                   : (grant.hasAsk || who.kind === 'user'
                      || who.kind === 'follow')
                     ? `${who.name} only gets what these realms cover. `
                       + 'Nothing is shared until you add one.'
                     : null}>
      <RealmChooser realms={preview.realms} selectedIds={grant.realmIds}
                    onSelect={grant.toggleRealm} noFit={preview.no_fit}
                    hasAsk={grant.hasAsk} onPreview={setPreviewRealm}
                    emptyIsNothing={who.kind === 'group'} />
      {/* THE ANSWER WHEN NONE OF THE ABOVE IS RIGHT. Only while nothing is picked,
          because that is the state it speaks to: once a realm is chosen the question
          becomes whether to widen it, which the line under the shortfall asks. Only
          for an app, too — a person or a group is not something you mint a realm
          "between you and" in this sense; those edges have their own pair realm
          machinery and no DLR to bound it with. */}
      {grant.hasAsk && who.kind === 'app' && grant.realmIds.length === 0 && (
        <SharedRealmOffer
          appName={who.name}
          owner={grant.actingAs ? "the group's" : 'your'}
          actorName={grant.actingAs?.name || null}
          exists={!!grant.pairRealmId}
          on={grant.placeIn === 'shared'}
          onToggle={(v) => grant.setPlaceIn(v ? 'shared' : 'none')}
        />
      )}
      {/* A verdict needs something to judge: nothing is said until a realm is in. */}
      {grant.hasAsk && grant.realmIds.length > 0 && (
        <Verdict
          met={askMet}
          total={askTotal}
          name={who.name}
        />
      )}
      {/* AND WHAT, EXACTLY, IT IS SHORT OF. The verdict gives the count; this
          names the cause, which is the part that tells you which realm to change.
          A realm is named only when ONE is added — with two the shortfall is a
          fact about their union, and blaming either by name would point at a
          realm that may cover the row perfectly well.

          IT SPEAKS THE VIEW'S OWN GRAMMAR. Under the prose it is sentences;
          under the table it is rows — type, the verbs that fell out, the cause —
          because three sentences beneath a mono table read as a different
          instrument playing the same note. Same data either way; only the voice
          changes, so neither view has to learn the other's.

          Note it does NOT move the list under the reader: it renders below the
          realm chooser, so it grows away from the control being clicked. That is
          what makes it safe here and not on the rows above, where the "not
          covered" line was removed for shifting the page mid-decision. */}
      {/* THE ROWS KEEP THEIR SPACE WHEN THEY ARE COVERED.
          They belong above the question — they are what it is about — and
          unmounting them is what made the control jump up the page as it was
          being read. Hidden instead: the gap they leave is blank and a little
          odd, and that is the price of the control staying exactly where the
          reader left it. */}
      {grant.hasAsk && grant.realmIds.length > 0 && (
        <Shortfall
          blockers={grant.blockers}
          hidden={askMet === askTotal}
          realmName={grant.realms.length === 1 ? grant.realms[0].name : null}
          variant={view === 'table' ? 'table' : 'prose'}
        />
      )}
      {/* UNDER THE ROWS IT ANSWERS. The rows above keep their space once they
          are covered, so answering this moves nothing over it. */}
      {grant.hasAsk && grant.realmIds.length > 0 && (
        /* A REALM THAT ALREADY REACHES EVERYTHING LOSES NO LIMIT, which is why
           `unbounded` is the server's list and not the page's reading of the ask:
           a warning about a limit that is not there teaches the reader to skip
           the line that matters, and this is the line that matters. */
        <Placement
          appName={who.name}
          owner={grant.actingAs ? "the group's" : 'your'}
          actorName={grant.actingAs?.name || null}
          unbounded={unbounding.length > 0}
          unboundedVerbs={unbounding}
          impact={widenImpact}
          count={grant.homeless.length}
          pairRealmId={grant.pairRealmId}
          target={grant.widenTarget}
          choices={grant.widenChoices}
          value={grant.placeIn}
          onChange={grant.setPlaceIn}
          onPickRealm={grant.chooseWidenRealm}
        />
      )}
    </Section>,
    // WHO YOU APPEAR AS. After the realm because the realm is what proposes an
    // identity: name one above it and the page has answered before the thing that
    // decides it was chosen. Two realms may propose two names — the one case only a
    // person can settle — and realms that propose none fall to the default.
    /* NO HINT. "Missions asked to read your identities" explained why the section
       is here, which its own heading and the choice under it already establish —
       and it is the reader's identity either way, asked for or not. */
    FACE: (
      <Section title="How you appear">
        <FaceChoice
          face={grant.face}
          identities={grant.faceOffer}
          owned={grant.identities.length}
          onPick={grant.setFaceId}
          onShowAll={grant.setShowAllFaces}
          onFollow={() => grant.setFaceId(null)}
          follow={(() => {
            const proposed = [...new Set((preview.realms || [])
              .filter((r) => r.profile_node_id && grant.realmIds.map(String).includes(String(r.id)))
              .map((r) => String(r.profile_node_id)))];
            const name = (id) => (grant.identities.find((i) => String(i.id) === id) || {}).name;
            if (proposed.length === 1) return { id: proposed[0], note: name(proposed[0]) || null };
            return proposed.length > 1 ? { id: null, note: 'the realms disagree' } : { id: null, note: 'your default' };
          })()}
          appName={who.name}
          username={user?.username}
          // Which chosen realm proposed each offered face, for the list's notes.
          sources={Object.fromEntries((preview.realms || [])
            .filter((r) => r.profile_node_id && grant.realmIds.map(String).includes(String(r.id)))
            .map((r) => [String(r.profile_node_id), r.name]))}
          everyone={grant.identities}
        />
      </Section>
    ),
    // A pair realm is named for the person, not for a part of you — printing
    // "→ TejasKrish" under "Which part of you / not yours to pick" asked a
    // question and then refused to answer it.
    /* IT SITS IN THE RAISED PANEL, like the chooser it is a settled version of.
       The panel used to mean strictly "the section you act in", and that reading
       is what this weakens: nothing here is choosable. What it buys is that the
       realm looks like the same object on both pages — a person meets the
       chooser and this within minutes of each other, and a realm that changes
       shape between them reads as two different mechanisms. So the panel means
       "the grant's scope", and the row inside says whether it is yours to set.

       NAME THE THING IT IS. "Where it goes" described a destination without
       saying what kind — and what it goes into is a PRIVACY REALM, the one noun
       this product uses for "a named part of you that can be shared". The app
       page spends a whole panel teaching that word; this page should not quietly
       call it something else two screens later. */
    /* A RUNTIME ASK NAMES A DESTINATION, not a part of you: "which part" was
       answered by the rows above (exactly those, nothing wider), and the panel
       says which realm receives them — the same noun the chooser uses. */
    C4L: grant.realmLock && (
      <Section tone="act"
               title={grant.item || who.runtime ? 'Which realm' : 'Which part of you'}>
        {/* THE SERVER'S REASON EXPLAINS A DESIGN DECISION — "naming one of your
            realms would promise everything it covers" — which answers a question
            the reader never asked, in the place where they are looking for what
            happens to their node. The item edge says what happens; every other
            edge keeps the server's wording. */}
        <RealmLocked title={grant.item
                       ? `The realm you and ${who.name} share`
                       : grant.realmLock.realm_name}
                     reason={grant.item
                       ? `Added to the exclusive realm you and ${who.name} share.`
                       : grant.realmLock.reason} />
      </Section>
    ),
    // Numbered like everything else. Rendered bare, it took a number out of the
    // sequence without showing one — the page counted 01, then 03.
    /* "SHARING", NOT "FILING". Filing is what happens to it afterwards — which
       realm it lands in — and that is the next section's business. The word the
       person is deciding about is the one they came here to do.
       NO HINT EITHER. "Just this one. Nothing else moves to Test." answered a
       question the page had not raised: a page showing one node, titled with one
       node, was never at risk of reading as though it moved anything else. */
    C6: grant.item && (
      <Section title="What you are sharing">
        {/* WHAT IT IS, then what the share allows. `one node` is the word that
            stops the row being read as its type: a schema row on the app page
            says what may be done to EVERY node of that type, and this says it
            about one. */}
        {/* The verbs moved to the key on the right, so the qualifier says only
            WHAT this is: a goal, and one of them. Saying "read only" here as
            well would be the row stating the same fact twice, in two
            registers. */}
        <Item title={grant.item.title}
              verbs={['read']}
              schema={grant.item.schema_name} />
      </Section>
    ),
    C7: <Section title={grant.hasAsk ? 'What it will see' : `What ${who.name} will see`}
                 // AN UNRESTRICTED ASK NAMED NO TYPES, so "the types it asked
                 // for" describes a list that does not exist — and this panel
                 // is the only place such an ask becomes countable. It says
                 // which types today, and that the ask does not end there.
                 hint={grant.hasAsk
                   ? (grant.asksAll
                     ? 'It asked for all of it. These are the types the chosen realms '
                       + 'reach today — ones you add later are covered too.'
                     : 'The types it asked for, and what the chosen realms grant on each.')
                   : 'Everything the realms you chose reach. This is what you are offering.'}
                 // No aside. It printed `realm.name` — the FIRST of however many
                 // were chosen — beside a heading describing the union of all of
                 // them, so three realms were credited to one.
                 >
      <Boundary inventory={grant.inventoryPlus} verbsByType={grant.verbsByType}
                // NO REALM MEANS NO GRANT, on every edge. `null` means "no
                // filter", which listed all 43 types under a heading that says
                // what the other side will see — before anything was granted.
                inAsk={!grant.realm ? []
                  : (grant.hasAsk ? grant.askSchemaIds : (grant.reachedSchemaIds || []))}
                // the reason a boundary is empty differs by edge, and the share
                // wording was appearing on ask edges that had a realm chosen
                empty={grant.hasAsk
                  ? (grant.realm
                    ? `${grant.realm.name} grants none of what ${who.name} asked for.`
                    : `Choose a realm above to see what ${who.name} would get.`)
                  : `Nothing yet. Choose a realm above and ${who.name} sees `
                    + 'what it reaches — until then, nothing.'}
                capabilities={grant.hasAsk ? null : grant.realmCapabilities}
                wasAsked={grant.hasAsk}
                notes={readNotesByName} />
    </Section>,
    // PUBLISHING IS THE RISK, so it is stated whether or not a realm fits. This
    // rendered only for `no_fit`, so the one edge with no counterparty to judge —
    // where the reader cannot ask "who is this?" because the answer is everyone —
    // was the one edge that carried no warning at all.
    C9: preview.no_fit ? (
      <Risk title="None of your realms cover this.">
        Nothing here can grant it until one does.{' '}
        <a className="gp-link" href={`${query.groupId ? `/group/${query.groupId}` : ''}/realms/realms?new=1`}
           target="_blank" rel="noopener noreferrer" onClick={() => setMadeOne(true)}>Make a realm</a>
        {' '}in a new tab, then come back: it will be here to choose.
      </Risk>
    ) : who.kind === 'public' ? (
      <Risk title="This goes to the open internet.">
        Anyone can read it without an account, and search engines can index it.
        You can revoke it, but you cannot un-see it — copies already made stay made.
      </Risk>
    ) : null,
    // THE TERMS COLLAPSE TO ONE CONTROL: when it expires.
    //
    // WHAT WENT, and it is not nothing: "Writes are signed as <app>, never as
    // you", "Every action enters your ledger — reads included", and "Revoking
    // is instant". Those were the page's only statements about attribution,
    // audit and revocation. They are all still TRUE — nothing here changes the
    // behaviour — but the page no longer says them, so a person learns those
    // properties somewhere else or not at all.
    //
    // The `node_scoped` row went with them, and that one was conditional and
    // load-bearing: it warned that a prior grant pinned to individual entries
    // would be re-granted at the TYPE level by saving from here. That is a
    // widening, and it is now silent.
    // THE RECEIPT sits last before the buttons: it is what you read back
    // immediately before deciding, and everything it itemises is settled by
    // then. Built from what the page already computes — no new data.
    // ONLY WHEN EVERY REQUIREMENT IS COVERED.
    //
    // `canConfirm` was too loose: it is true as soon as ONE row survives the
    // realm, so the receipt appeared mid-decision and then grew as more realms
    // were added — the up-and-down that made the page hard to follow. A receipt
    // is a record of a settled thing. Until the ask is fully covered the choice
    // is still open, and there is nothing to record.
    /* SHOWN WHENEVER THERE IS SOMETHING TO HAND OVER, which is not the same as
       "whenever an ask is fully covered". On an app grant the receipt waits for
       the ask to be met, because a partial grant is a decision still in
       progress. On an edge with NO ask — joining a group — there is nothing to
       meet: what you hand over is exactly what the realms you ticked reach, and
       that was the one thing the page never said. */
    RECEIPT: (grant.canConfirm && (askTotal === 0 || askMet === askTotal)) ? (
      <Receipt
        rows={(grant.inventoryPlus || [])
          .map((r) => ({ name: r.schema_name, count: r.count,
                         verbs: [...new Set(grant.verbsByType[r.schema_name] || [])].sort(),
                         notes: readNotesByName[r.schema_name] }))
          .filter((r) => r.verbs.length)
          .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name))}
        /* WITH NO ASK, THE REALMS' OWN CAPABILITIES. A realm grants more than
           types — one permitting `plugins:execute` grants it to a friend too —
           and C7 said so on the share edges. The Receipt replacing it there has
           to, or a capability goes out unmentioned. */
        actions={grant.hasAsk
          ? (categorizeDlr(preview.ask.items || [], [], null, {}).capabilities || [])
            .map((c) => ({ text: c.text, tags: c.detailTags || [] }))
          : (grant.realmCapabilities || []).map((v) => ({ text: v.replace(':', ' '), tags: [] }))}
        total={grant.grantedEntries}
        /* EVERYTHING YOU HAVE — the denominator the receipt's own figures are
           a part of. `inventoryPlus` is the same list C7 used to draw the
           whole against; the Receipt needs it for the reach figure and for
           nothing else. */
        inventory={grant.inventoryPlus || []}
        /* A runtime ask is issued under the grant it joins, not under a realm
           the person chose — there was no chooser. */
        realmLabel={who.runtime
          ? `${grant.actingAs ? "the group's" : 'your'} existing grant`
          : grant.realmIds.length === 1
            ? (grant.realm ? grant.realm.name : '')
            : `${grant.realmIds.length} realms`}
        /* THE DATE, NOT THE WORD. "Unchanged" describes the CONTROL — it
           says you did not touch the select. A receipt has to state the term
           itself, so `keep` resolves to the date being kept. */
        /* A RUNTIME ASK KEEPS THE STANDING GRANT'S TERM — there is no expiry
           control on that edge, so the select's default ('30d') would put a
           date on the receipt that nothing is going to write. The term is the
           grant's own, whatever it was. */
        expiry={UNDATED_KINDS.has(who.kind) ? 'Never' : who.runtime
          ? (preview.existing?.expires_at
            ? dateOf(preview.existing.expires_at) : 'Never')
          : expiry === 'keep'
            ? dateOf(preview.existing?.expires_at)
            : (EXPIRY.find((o) => o.value === expiry) || {}).label}
      />
    ) : null,
    C10: (() => {
      const prior = preview.existing?.expires_at;
      /* A RUNTIME ASK STATES ITS TERM, IT DOES NOT SET ONE. The ask is a row
         in the runtime-requests table, answered yes or no; saying yes adds it
         to the grant the person already made, whose expiry was settled then.
         So this slot is the expiry read back, in the same labelled-pair
         register as the app edge's terms, with no control in it. Nothing
         else: an "added to <grant from date>" pair was tried here and read
         as a thing to understand rather than a fact to glance at — the
         banner above already says this joins the grant they made. No realm
         is named: the person is not choosing one. */
      if (UNDATED_KINDS.has(who.kind)) {
        return (
          <div className="gp-term" role="note" data-testid="undated-terms">
            <span className="gp-term__k">Expires</span>
            <span className="gp-term__v">
              {who.kind === 'sub_entity'
                ? `As long as ${who.parent_app_name || 'the app'} has access — you can stop it any time`
                : 'Never — you can stop it any time'}
            </span>
          </div>
        );
      }
      if (who.runtime) {
        return (
          <div className="gp-term" role="note" data-testid="runtime-terms">
            <span className="gp-term__k">Expires</span>
            <span className="gp-term__v">
              {prior ? `As the grant does, ${dateOf(prior)}` : 'Never — as the grant does'}
            </span>
          </div>
        );
      }
      // KEYED ON THE DATE, NOT `priorState`. preview.py's state ladder checks
      // revoked_at BEFORE expires_at, so a grant that was revoked AND had
      // lapsed reports 'revoked' — and a note keyed on the state would miss
      // exactly that case.
      const keepable = canKeep(prior);
      return (
        <label className="gp-expires gp-term">
          <span className="gp-term__k">Expires</span>
          <select className="gp-expiry gp-term__v" value={expiry} aria-label="Expires"
                  onChange={(e) => setExpiry(e.target.value)}>
            {/* NAME THE DATE. "Keep the current date" never said WHICH date,
                which is how one that had already passed sat here looking
                perfectly reasonable. */}
            {keepable && (
              <option value="keep">{`Keep ${dateOf(prior)}`}</option>
            )}
            {expiryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* A lapsed term is not offered back, so say why it vanished rather
              than leaving the person to wonder what happened to their date. */}
          {prior && !keepable && (
            <small className="gp-expires__note">
              {`The old end date, ${dateOf(prior)}, has passed. Choose a new one.`}
            </small>
          )}
        </label>
      );
    })(),
    C11: needsKey ? (
      <Unlock appName={who.name} busy={submitting} error={keyError}
              onUnlock={onUnlock}
              onCancel={() => { setNeedsKey(false); setKeyError(null); }} />
    ) : (
      <>
        {submitError && (
          <div className="gp-route__err" role="alert">
            <p className="gp-route__errmsg">{submitError}</p>
            {/* NAMED, because a refusal you cannot act on is only half a refusal.
                The server knows exactly which type and verb fell outside the chosen
                realms; the page used to render the summary alone, so the one type at
                fault stayed unnamed while sitting in the list right above. */}
            {submitViolations && (
              <ul className="gp-route__viol">
                {submitViolations.map((v) => <li key={v}>{v}</li>)}
              </ul>
            )}
          </div>
        )}
        {/* WHY IT IS BLOCKED goes here, not in the button. The label used to
            carry the state — "Allow the 15" — which put an internal count of
            granted ask rows on the one control that matters, and answered a
            question nobody asked. The button says what happens; the line above
            says why it can't yet. */}
        <Decide
          confirmLabel={confirmLabel}
          denyLabel={groupStep === 'made' ? 'Not now' : groupStep === 'join' ? 'Not now' : groupStep === 'edit' ? 'Cancel' : 'Deny'}
          disabled={!grant.canConfirm || submitting || awaitingWidenAnswer}
          // CALLED WITH NO ARGUMENT, deliberately. `onClick={onApprove}` hands the
          // click event to the first parameter — `keysOverride`, the encryption
          // retry's already-unlocked keys — so every press looked like a retry
          // that had been past the checks once already.
          onConfirm={() => onApprove()}
          // DENY IS ALSO AN ANSWER, and the app is waiting for it. Going `back`
          // left the app on its consent-pending screen with no idea the person
          // said no; every one of them reads `error=access_denied` (a person
          // share reads `denied=true`) and shows a real message for it.
          onDeny={async () => {
            // A RUNTIME "NO" IS RECORDED. Leaving the batch pending told the
            // app nothing — it could not tell "declined" from "not answered
            // yet" and kept asking. /authorize/expand stores the denial; the
            // sweep would expire the row eventually, so the write is best
            // effort and never stands between the person and the way back.
            // Handed back bare, like the grant: the app's return route reads
            // its own state and finds out for itself what it may read now.
            // A PARKED PLUGIN RUN'S "NO" IS RECORDED too: it is declined, not
            // left pending until it expires.
            if (who.kind === 'plugin' && who.request_id) {
              try {
                await requestsApi.deny(who.id, who.request_id, who.group?.id || query.groupId || null);
              } catch { /* it expires either way */ }
              navigate(-1);
              return;
            }
            if (who.runtime?.request_id) {
              try {
                await api.expandAuthorization({
                  client_id: who.client_id,
                  request_id: who.runtime.request_id,
                  decision: 'deny',
                });
              } catch { /* the sweep expires it either way */ }
              if (redirectUri) window.location.href = redirectUri;
              else navigate('/authorized-apps');
              return;
            }
            // The group exists by now and its empty contract with it: not now
            // goes into it, not back to wherever the history says (a fresh tab
            // has none of ours).
            if (groupStep === 'made' && !redirectUri) { navigate(`/group/${who.id}/graph`); return; }
            if (!redirectUri) { navigate(-1); return; }
            const sep = redirectUri.includes('?') ? '&' : '?';
            const reason = who.kind === 'user' || who.kind === 'follow'
              ? 'denied=true' : 'error=access_denied';
            window.location.href = `${redirectUri}${sep}${reason}`;
          }}
        />
      </>
    ),
  };

  // C3 only when there is an ask to state: a group join makes no requirements,
  // and the heading asserted a list over an empty section.
  const entry = (who.kind === 'app' || who.kind === 'sub_entity') ? registerEntry(who) : null;
  const bondState = !grant.realmIds.length ? 'none'
    : (askTotal > 0 && askMet === askTotal) || (!grant.hasAsk) ? 'full' : 'some';
  /* A PERSON OR A GROUP IS NAMED, NOT MARKED. Both sides of the share are on
     ofSelf, so no mark distinguishes them: the figure is your username, the
     exchange, and theirs — a person by username, so two people who share a
     display name still read as two; a group by its name. */
  const name = (label, handle) => (
    <span className={`gp-bond__name${handle ? ' is-handle' : ''}`}>{label}</span>
  );

  /* YOU ARE NAMED ON EVERY EDGE, and the other side is drawn when the register
     holds its mark, named when it does not.

     The app edges used to put the ofSelf S where your username goes, which made
     "you" two different things depending on who was asking — a mark against an
     app, a name against a person. It also said the wrong thing: what an app
     receives is YOUR data, and the S on that side reads as ofSelf handing it
     over, which is the broker framing these figures rejected on the person
     edges. One rule now covers all fifteen.

     It also fixes a figure that said nothing. An app the register does not hold
     contributed NO side at all, so the page opened on a lone S with no sign of
     who was asking — which is every app but the four in APP_FIGURES. No mark is
     ever invented for one; it is named instead. */
  /* THE LEFT SIDE IS WHOSE DATA IT IS, which is not always you.
   *
   * On a group edge you are acting FOR the group: `acting_as` is set, C2 says so
   * in words, and the graph being opened is the group's. Naming yourself there
   * would put the wrong owner on the one figure whose whole job is to say who is
   * on each side — and it would contradict the line directly beneath it.
   *
   * A username is set in mono (a handle is a literal string, and two people with
   * the same display name must still read as two); a group is a display name and
   * set like one, the same rule the right-hand side follows. */
  /* `acting_as` is the SERVER's answer, not the picker's. Choosing an actor
   * rewrites `group_id` and refetches, and a switch that fails goes back to the
   * owner that worked — so following the selection here would name a group the
   * preview never agreed to. The word for the personal case is the one
   * `actorsFor` uses, because ActingChoice can be on screen saying it too. */
  const youName = grant.actingAs
    ? name(grant.actingAs.name, false)
    : (user?.username ? name(user.username, true) : name('You', false));
  const asksAsApp = who.kind === 'app' || who.kind === 'sub_entity';
  /* THE AGENT IS THE PARTY ON A SUB-ENTITY EDGE, so the agent is what the figure
   * names. It used to name the PARENT — `priya ⟶ Personas` over a headline
   * reading "scout wants to act under Personas" — which put the one party the
   * grant is not being made to on the receiving end of the arrow. A sub-entity's
   * EP is capped BELOW its app's; drawing the app there says the opposite.
   *
   * IT TAKES NO MARK, and that is the cost. `registerEntry` resolves a
   * sub-entity through `parent_app_name`, so the only figure available is the
   * parent's — and a parent's mark captioned with the agent's name would put two
   * parties on one side, which is the thing being fixed. An agent has no mark of
   * its own, so it is named, like a person or a group.
   *
   * The parent is not lost: the headline says "wants to act under Personas",
   * which is where that relationship belongs. Repeating it here would be the
   * restatement just taken out of the strip below. */
  const appLabel = who.name;
  const drawsMark = entry && hasAppFigure(entry) && who.kind !== 'sub_entity';
  const figure = asksAsApp ? (
    drawsMark
      /* `names` is off: the mark still needs its 128px box, and a text `you`
         centres inside that box on its own. */
      ? <Bond state={bondState} you={youName}
              app={(viewBox) => <AppMark app={entry.id} animate viewBox={viewBox} />} />
      : <Bond state={bondState} names you={youName} app={() => name(appLabel, false)} />
  ) : null;

  /* A PERSON OR A GROUP HAS NO MARK in the register, so on those edges the page
     says who is on each side in words.
     THE PUBLIC EDGE IS ONE OF THEM. "Anyone on the internet" is the widest
     counterparty the page can name and it was the only one left with no figure
     at all — the lone ofSelf S, which said nothing about who was receiving.
     It has a name like the rest, so it is named like the rest. */
  const between = (who.kind === 'group' || who.kind === 'user'
    || who.kind === 'follow' || who.kind === 'public');
  const namesFigure = between ? (
    <Bond state={bondState} names
          you={youName}
          app={() => (who.username ? name(who.username, true) : name(who.name, false))} />
  ) : null;

  return <GrantPage edge={edge} slots={slots} figure={figure || namesFigure}
                    // Editing your share shows only that half: what you can do is
                    // the group's, and not what this page changes.
                    has={{ C9: preview.no_fit, C3: grant.hasAsk, ROLE: !!standing && groupStep !== 'edit',
                           // Absent for anyone who administers no groups, so
                           // their page is byte-for-byte what it is today.
                           C2: !!grant.actingAs || hasChoice(actors) }}
                    className="gp-route" />;
}

export { sequenceFor };
